import { useEffect, useState } from "react";
import { Check, Copy, Link2, Share2, X } from "lucide-react";
import { toast } from "sonner";

import {
  buildShareTargets,
  copyToClipboard,
  publicListingUrl,
  type ShareChannel,
} from "@/lib/share";

const CHANNEL_CLASS: Record<ShareChannel, string> = {
  whatsapp: "bg-[hsl(142_70%_35%)] text-white",
  facebook: "bg-[hsl(221_44%_41%)] text-white",
  x: "bg-brown-dark text-white",
  telegram: "bg-[hsl(200_79%_46%)] text-white",
};

export interface ShareListingInfo {
  id: string;
  title: string;
  priceLabel?: string | null;
}

export function ShareDialog({
  listing,
  open,
  onClose,
}: {
  listing: ShareListingInfo;
  open: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const url = publicListingUrl(listing.id);
  const text = [listing.title, listing.priceLabel].filter(Boolean).join(" — ");
  const targets = buildShareTargets(url, text);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const copy = async () => {
    const ok = await copyToClipboard(url);
    setCopied(ok);
    toast[ok ? "success" : "error"](ok ? "Link copied to clipboard." : "Could not copy the link.");
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-brown-dark/40 p-0 sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Share listing"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-3xl border border-border bg-card p-4 shadow-soft sm:rounded-3xl sm:p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-brown-dark">Share this listing</h2>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{listing.title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close share dialog"
            className="rounded-full p-1.5 text-muted-foreground transition hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2">
          {targets.map((t) => (
            <a
              key={t.key}
              href={t.href}
              target="_blank"
              rel="noreferrer noopener"
              className={`flex flex-col items-center gap-1.5 rounded-2xl px-2 py-3 text-[11px] font-semibold transition hover:brightness-110 ${CHANNEL_CLASS[t.key]}`}
            >
              <Share2 className="h-4 w-4" />
              {t.label}
            </a>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-2xl border border-border bg-secondary/50 p-2">
          <Link2 className="ml-1 h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            readOnly
            value={url}
            aria-label="Public listing link"
            onFocus={(e) => e.currentTarget.select()}
            className="min-w-0 flex-1 bg-transparent text-xs text-brown-dark outline-none"
          />
          <button
            type="button"
            onClick={copy}
            className="inline-flex shrink-0 items-center gap-1 rounded-xl bg-brand px-3 py-2 text-xs font-bold text-brown-dark transition hover:brightness-95"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>

        <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
          Anyone with this link can view the listing, its map location and send an enquiry — no
          account needed.
        </p>
      </div>
    </div>
  );
}

/** Small pill button that opens the share sheet. Use on approved listings. */
export function ShareButton({
  listing,
  className,
  label = "Share",
}: {
  listing: ShareListingInfo;
  className?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          "inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
        }
      >
        <Share2 className="h-3.5 w-3.5" /> {label}
      </button>
      <ShareDialog listing={listing} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
